import Alert from "@mui/material/Alert";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import Loader from "_shared/_views/loading/loader";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import {
  BulkInvitationFieldOption,
  BulkInvitationRowValues,
  readText,
} from "legal-entities/invitations/bulk/bulk-invitation-column-spec";
import {
  TeamMemberRosterRow,
  useInviteTeamMembersMutation,
} from "legal-entities/invitations/bulk/_api/bulk-invitation-api-service";
import BulkInvitationGrid from "legal-entities/invitations/bulk/_views/bulk-invitation-grid";
import createTeamMemberRosterColumns from "legal-entities/invitations/bulk/team-member-roster-columns";
import { useGetVendorRolloutBoardQuery } from "legal-entities/vendor-rollouts/_api/vendor-rollout-board-api-service";
import PermissionTemplateQuery from "permissions/_api/request-contracts/permission-template-query";
import { useGetPermissionTemplatesQuery } from "permissions/_api/permission-templates-api-service";
import "permissions/__extensions/users/user-extensions";
import useSessionUser from "permissions/session/use-session-user";
import { useMemo, useState } from "react";
import { useParams } from "react-router";

const PageContainer = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  margin: theme.spacing(1),
  overflow: "auto",
  padding: theme.spacing(2),
}));
const PageTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));
const ClientChoice = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  maxWidth: "32rem",
}));

/** What the select's "no particular client" option stores, since a `Guid` cannot be empty. */
const NO_ROLLOUT = "";

function toRow(values: BulkInvitationRowValues): TeamMemberRosterRow {
  const accessTemplateId = readText(values, "accessTemplateId");
  return {
    email: readText(values, "email"),
    firstName: readText(values, "firstName"),
    lastName: readText(values, "lastName"),
    title: readText(values, "title"),
    requestedMarketplaceVisibility: values.requestedMarketplaceVisibility === true,
    accessTemplateId: accessTemplateId.length > 0 ? accessTemplateId : undefined,
  };
}

/**
 * A firm's team leader listing every colleague who will work and bill on a client's matters.
 *
 * Gated on administration access rather than on a gate of its own: entering thirty colleagues is
 * the same authority as entering one thirty times, and that is exactly what the API asserts.
 *
 * The client this list belongs to is asked for rather than passed in. A firm can be rolled out by
 * several clients at once and each roll-out names a different client in form letter 2, so the
 * answer is not derivable from the firm - it is the one thing only the person entering the list
 * knows. The choices come from the roll-out board scoped to this firm, which is the firm's own
 * entity-wide read of the same rows its clients see from the other side.
 */
export default function TeamMemberRosterPage() {
  const user = useSessionUser();
  // Present only when the page was reached from the prompt raised on the Attorney Hub proposal's
  // hire, whose topic is the firm. See the route in index.tsx.
  const namedEntityId = useParams().entityId;
  const [inviteTeamMembers] = useInviteTeamMembersMutation();
  const [chosenRolloutId, setChosenRolloutId] = useState<string | null>(null);

  const templatesQuery = useMemo(() => new PermissionTemplateQuery({}), []);
  const { data: templatesPage } = useGetPermissionTemplatesQuery({ query: templatesQuery });

  // Skipped until there is a session, because the board decides scope from the id it is sent and
  // an absent one is the staff question - every client at once, which this caller cannot ask.
  const rolloutsQuery = useGetVendorRolloutBoardQuery(
    { vendorLegalEntityId: user?.legalEntityId },
    { skip: !user },
  );

  // Freshly allocated on every render and read by the `useMemo` below, so it is memoized
  // deliberately rather than defensively: a new array each render would rebuild the spec each
  // render, and the grid's columns with it. See app/CLAUDE.md, React Compiler.
  const templateOptions = useMemo<readonly BulkInvitationFieldOption[]>(
    () =>
      (templatesPage?.data ?? [])
        .filter((template) => Boolean(template.id))
        .map((template) => ({ value: String(template.id), label: template.name })),
    [templatesPage?.data],
  );
  const spec = useMemo(() => createTeamMemberRosterColumns(templateOptions), [templateOptions]);

  // Closed roll-outs are already off the board, so every row here is a client the firm is
  // currently being rolled out for.
  const rollouts = useMemo(
    () => (rolloutsQuery.data?.rows ?? []).map((row) => ({ id: row.rolloutId, clientName: row.clientName })),
    [rolloutsQuery.data?.rows],
  );

  if (!user) return <Loader text="Loading..." />;
  if (!user.canAccessAdministration()) return <NotAuthorized />;

  const entityId = user.legalEntityId;
  // A prompt names the firm it is about. Following one that names a different firm - a forwarded
  // link, a leader who has since moved - must say so, rather than quietly opening the reader's own
  // roster and inviting people into an organization the link was not about.
  if (namedEntityId && namedEntityId.toLowerCase() !== entityId.value.toLowerCase()) {
    return <NotAuthorized />;
  }

  // One open roll-out is not a choice, it is a fact, so it is stated rather than asked. Asking
  // would invite the leader to get their only answer wrong, and a wrong one reaches a colleague
  // as another client's name.
  const onlyRollout = rollouts.length === 1 ? rollouts[0] : undefined;
  const selectedRolloutId = onlyRollout?.id ?? chosenRolloutId ?? NO_ROLLOUT;
  const vendorRolloutId = selectedRolloutId.length > 0 ? new Guid(selectedRolloutId) : undefined;

  return (
    <PageContainer>
      <PageTitle variant="h5">Invite your colleagues</PageTitle>

      {onlyRollout && (
        <Alert severity="info" sx={{ mb: 3, maxWidth: "32rem" }}>
          Everyone below is invited for <strong>{onlyRollout.clientName}</strong>, the client rolling
          your firm out. Their invitation names that client.
        </Alert>
      )}

      {rollouts.length > 1 && (
        <ClientChoice
          select
          label="Which client is this list for?"
          value={selectedRolloutId}
          onChange={(event) => setChosenRolloutId(event.target.value)}
          helperText="Their invitation names this client. Choose the last option for colleagues who are not joining for a particular client."
        >
          {rollouts.map((rollout) => (
            <MenuItem key={rollout.id} value={rollout.id}>
              {rollout.clientName}
            </MenuItem>
          ))}
          <MenuItem value={NO_ROLLOUT}>Not for a particular client</MenuItem>
        </ClientChoice>
      )}

      {/* A firm whose own board cannot be read still gets to enter its roster; what it loses is
          the client's name in the letter, not the colleagues' accounts. */}
      {rolloutsQuery.isError && (
        <Alert severity="info" sx={{ mb: 3, maxWidth: "32rem" }}>
          Attorney Hub could not look up which clients are rolling your firm out, so everyone below
          gets the standard invitation rather than one naming a client.
        </Alert>
      )}

      <BulkInvitationGrid
        spec={spec}
        submitLabel="Send invitations"
        description={
          "Everyone who will work and bill on your client's matters. Nobody can bill for work " +
          "until they have an account and a profile listing their credentials, so the list is " +
          "worth getting complete in one go."
        }
        onSubmit={(rows) =>
          inviteTeamMembers({
            entityId,
            vendorRolloutId,
            rows: rows.map(toRow),
          }).unwrap()
        }
      />
    </PageContainer>
  );
}
